import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { CompanyFilter } from "../../../../../legacy/types/companyFilter";
import { ProductsQueryPayload } from "../../../../../types/taxonomy";
import { PRODUCT_ID_QUERY } from "../../../../../apis/taxonomy/queries";
import { TAXONOMY_CLIENT } from "../../../../../utils/consts";
import {
  getExcludedFilterValue,
  getIncludedFilterValues,
  generateInclusionLabel,
  flatInclusionFilterIds,
} from "../utils";

// Helper to transform product data into a name map
const productDataToNameMap = (
  products: ProductsQueryPayload["product"]["data"] | undefined
): Record<string, string> => {
  if (!products) return {};

  return products.reduce(
    (acc, product) => {
      if (product && product.id && product.name) {
        acc[String(product.id)] = product.name;
      }
      return acc;
    },
    {} as Record<string, string>
  );
};

export const useProductsLabel = (filter: CompanyFilter["products"]) => {
  const includeFilters = useMemo(
    () => getIncludedFilterValues(filter || []),
    [filter]
  );
  const excludeFilter = useMemo(
    () => getExcludedFilterValue(filter || []),
    [filter]
  );

  const includedIds = useMemo(
    () => flatInclusionFilterIds(includeFilters).map(Number),
    [includeFilters]
  );
  const excludedIds = useMemo(
    () => excludeFilter?.ids?.map(Number) || [],
    [excludeFilter]
  );

  const { data: productQueryData, loading: productsLoading } =
    useQuery<ProductsQueryPayload>(PRODUCT_ID_QUERY, {
      variables: {
        ids: [...includedIds, ...excludedIds],
      },
      skip: includedIds.length === 0 && excludedIds.length === 0,
      context: {
        clientName: TAXONOMY_CLIENT,
      },
    });

  const nameMap = useMemo(
    () => productDataToNameMap(productQueryData?.product.data),
    [productQueryData]
  );

  // If there are IDs but names are still loading, return a placeholder or empty
  if ((includedIds.length > 0 || excludedIds.length > 0) && productsLoading) {
    return "Loading..."; // Or an empty string: ""
  }

  return generateInclusionLabel(includedIds, excludedIds, nameMap);
};
